import { Children, cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import type { ResultOptionProps } from './components/ResultsOption';

import { ResultOption } from './components/ResultsOption';
import styles from './SearchField.module.css';
import { ResultOptionBox } from './components/ResultOptionBox';

import { Icon } from 'components/Icon';
import { useOnClickOutside } from 'components/hooks/useOnClickOutside';

export interface SearchFieldProps {
  /** Unique identifier for the search field. */
  id: string;
  /** Placeholder text for the search input. */
  placeholder: string;
  /** Indicates if the search field is in a loading state. */
  isLoading?: boolean;
  /** If true, the search field is displayed in a compact style. */
  isCompact?: boolean;
  /** Callback function triggered when the input value changes. */
  onChange: (query: string) => void;
  /** The children elements, typically ResultOption components. */
  children: ReactNode;
  /** Optional row to display the count of results. */
  resultCountRow?: ReactNode;
}

export const SearchField = ({
  id,
  placeholder,
  isLoading = false,
  isCompact,
  onChange,
  children,
  resultCountRow,
}: SearchFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showField, setShowField] = useState<boolean>(false);
  const optionListRef = useRef<HTMLDivElement>(null);

  const handleReset = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.value;
    target.setSelectionRange(value.length, value.length);
    if (value) {
      onChange(value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onChange('');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        if (isCompact) {
          setShowField(false);
          inputRef.current?.blur();
        }
        break;
      case 'ArrowDown': {
        event.preventDefault();
        const container = optionListRef.current;
        const firstFocusable = container?.querySelector<HTMLElement>('li[role="option"]:not([tabindex="-1"])');
        if (firstFocusable) {
          let currentOption: HTMLElement | null = firstFocusable;
          while (currentOption && currentOption.getAttribute('tabindex') === '-1') {
            currentOption = currentOption.nextElementSibling as HTMLElement;
          }
          currentOption?.focus();
        }
        break;
      }
    }
  };

  useEffect(() => {
    if (!Children.count(children)) {
      setShowField(false);
    }
  }, [children]);

  useOnClickOutside(optionListRef as RefObject<HTMLElement>, onChange.bind(null, ''));

  return (
    <div className={styles['search-field__input-wrapper']}>
      {isCompact && (
        <button
          type="button"
          className={styles['search-field__compact-button']}
          onClick={() => setShowField(true)}
          aria-label={placeholder}
        >
          <Icon name="Magnify" className={styles['search-field__compact-icon']} aria-hidden="true" />
        </button>
      )}
      <div
        className={clsx(styles['search-field__input-container'], {
          [styles['search-field__input-container--hidden']]: isCompact && !showField,
          [styles['search-field__input-container--visible']]: isCompact && showField,
        })}
      >
        <Icon name="Magnify" className={styles['search-field__icon']} aria-hidden="true" />
        <input
          type="text"
          className={styles['search-field__input-field']}
          placeholder={placeholder}
          id={id}
          onChange={(event) => onChange(event.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={Boolean(Children.count(children)) || isLoading}
          aria-controls={`${id}-results`}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-label={placeholder}
          autoComplete="off"
          ref={inputRef}
        />
      </div>
      {(children || isLoading) && (
        <ResultOptionBox resultCountRow={resultCountRow ? resultCountRow : null} ref={optionListRef}>
          {Children.map(children, (child, index) => {
            if (isValidElement<ResultOptionProps>(child)) {
              return cloneElement(child, {
                key: index,
                onReset: handleReset,
              });
            }
            return child;
          })}
        </ResultOptionBox>
      )}
    </div>
  );
};

SearchField.ResultOption = ResultOption;
